import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MapPanAndZoomStream } from 'src/app/feature/map-viewer/common/map-pan-zoom-stream.service';
import { MapService } from 'src/app/feature/map-viewer/map.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { FullFeatureFilter } from 'src/app/shared/common/feature-filter/full-feature-filter';
import { FeatureService } from '../map-data-services/feature/feature.service';
import { SurveyJob } from './survey-job';
/**
 * Service to store and manage workspace survey jobs
 * Maintains the collection of survey jobs for the current workspace
 * and tracks loading state
 */
@Injectable({
    providedIn: 'root'
})
export class SurveyJobStoreService {
    /**
     * Stream of all survey jobs in the current workspace
     * Updated when workspace changes or jobs are loaded
     */
    mapWorkspaceSurveyJobsStream = new BehaviorSubject<SurveyJob[]>([]);

    /**
     * Stream to track loading state of survey jobs
     * True when jobs are being fetched from API, false otherwise
     */
    mapWorkspaceSurveyJobsLoadingStream = new BehaviorSubject<boolean>(false);

    constructor(
        private projectStream: ProjectStreamService,
        private featureService: FeatureService,
        private mapService: MapService,
        private mapPanAndZoomStream: MapPanAndZoomStream
    ) {}

    /**
     * Set the survey jobs for the current workspace
     * @param jobs - Array of survey jobs to store
     */
    public setMapWorkspaceSurveyJobs(jobs: SurveyJob[]): void {
        this.mapWorkspaceSurveyJobsStream.next(jobs);
    }

    /**
     * Set the loading state
     * @param loading - True if loading, false otherwise
     */
    public setLoading(loading: boolean): void {
        this.mapWorkspaceSurveyJobsLoadingStream.next(loading);
    }

    /**
     * Get current workspace survey jobs
     * @returns Array of current survey jobs
     */
    public getMapWorkspaceSurveyJobs(): SurveyJob[] {
        return this.mapWorkspaceSurveyJobsStream.getValue();
    }

    /**
     * Clear all survey jobs
     * Called when workspace changes or component is destroyed
     */
    public clearSurveyJobs(): void {
        this.mapWorkspaceSurveyJobsStream.next([]);
    }

    /**
     * Zoom and pan the map to show all features for this job
     * Similar to layer's zoomToLayerExtents functionality
     */
    public async zoomToJobFeatures(jobId: string): Promise<void> {
        try {
            // Get current project ID
            const projectId = this.projectStream.getCurrentProject()?.id;
            if (!projectId) {
                return;
            }
            // Build filter for job features
            const filter = new FullFeatureFilter();

            // Filter by this specific job using jobFeatures
            filter.jobFeatures = { jobIds: [jobId], featureIds: [] };

            // Get bounds for all features in this job
            const bounds = await this.featureService.getBoundsOfFeatures(projectId, filter);

            let basemapMode = this.mapService.selectedBaseMapMode;
            // Shift bounds area 20% to the right by adjusting padding as the Job panel is wider than the Layer panel.
            // Increase left padding to push content right, decrease right padding to allow more space
            // Original: [360, 60] left, [340, 10] right
            // New: [540, 60] left, [160, 10] right - shifts view ~20% right
            this.mapPanAndZoomStream.zoomToBounds(bounds, [540, 60], [160, 10], {
                maxZoom: basemapMode.maxZoom
            });
        } catch (error) {
            // Silently handle errors (job might not have features)
            console.warn('Could not zoom to job features:', error);
        }
    }
}
